import { useState, useEffect } from 'react';
import { bookingsAPI, propertiesAPI } from '../../lib/api';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { formatCurrency, formatDate, formatDateRange } from '../../lib/utils';
import {
  MoreVertical,
  CalendarCheck,
  Loader2,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  CreditCard,
  Mail,
  Phone,
  RefreshCw,
  History,
  CalendarDays,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { BookingActionModal } from '../../components/BookingActionModal';

const statusConfig = {
  confirmed: { label: 'Confirmed', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800 border-red-200' },
  completed: { label: 'Completed', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  no_show: { label: 'No Show', color: 'bg-gray-100 text-gray-800 border-gray-200' },
};

const paymentStatusConfig = {
  pay_at_property: { label: 'Pay at Property', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  refunded: { label: 'Refunded', color: 'bg-gray-100 text-gray-800 border-gray-200' },
};

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProperty, setFilterProperty] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [actionModal, setActionModal] = useState(null);

  const fetchData = async () => {
    try {
      const [bookingsRes, propsRes] = await Promise.all([
        bookingsAPI.list(),
        propertiesAPI.list(),
      ]);
      setBookings(bookingsRes.data);
      setProperties(propsRes.data);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      await bookingsAPI.updateStatus(bookingId, newStatus);
      toast.success(`Booking status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking status');
    }
  };

  const handleRetryChannex = async (bookingId) => {
    try {
      const result = await bookingsAPI.retryChannex(bookingId);
      if (result.data.status === 'success') {
        toast.success('Booking synced to Channex successfully');
        fetchData();
      } else if (result.data.status === 'already_synced') {
        toast.info('Booking is already synced to Channex');
      } else {
        toast.error(result.data.message || 'Failed to sync to Channex', {
          description: result.data.details ? 'Check if Booking CRS App is installed in Channex' : undefined
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retry Channex sync');
    }
  };

  const viewDetails = (booking) => {
    setSelectedBooking(booking);
    setDetailsDialogOpen(true);
  };

  const filteredBookings = bookings.filter((booking) => {
    if (filterProperty !== 'all' && booking.property_id !== filterProperty) return false;
    if (filterStatus !== 'all' && booking.status !== filterStatus) return false;
    if (dateFrom && booking.check_in < dateFrom) return false;
    if (dateTo && booking.check_in > dateTo) return false;
    return true;
  });

  const hasDateFilter = dateFrom || dateTo;
  const clearDateFilter = () => { setDateFrom(''); setDateTo(''); };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="page-wrapper" data-testid="bookings-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Bookings</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage all reservations and booking status.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={filterProperty} onValueChange={setFilterProperty}>
              <SelectTrigger className="w-[180px]" data-testid="filter-property">
                <SelectValue placeholder="Filter by property" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]" data-testid="filter-status">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="no_show">No Show</SelectItem>
              </SelectContent>
            </Select>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant={hasDateFilter ? 'default' : 'outline'} className="gap-2" data-testid="filter-date-range">
                  <CalendarDays className="h-4 w-4" />
                  {hasDateFilter ? `${dateFrom || '...'} — ${dateTo || '...'}` : 'Date Range'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-4" align="end">
                <div className="space-y-3">
                  <p className="text-sm font-medium">Filter by check-in date</p>
                  <div className="flex items-center gap-2">
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">From</label>
                      <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[150px]" data-testid="date-from" />
                    </div>
                    <span className="text-muted-foreground mt-5">—</span>
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">To</label>
                      <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[150px]" data-testid="date-to" />
                    </div>
                  </div>
                  {hasDateFilter && (
                    <Button variant="ghost" size="sm" onClick={clearDateFilter} className="text-xs w-full" data-testid="clear-date-filter">
                      <X className="h-3 w-3 mr-1" /> Clear dates
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Bookings table */}
        {filteredBookings.length === 0 ? (
          <div className="bg-white rounded-lg border border-border p-12 text-center">
            <CalendarCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No bookings found</h3>
            <p className="text-muted-foreground">
              {bookings.length === 0
                ? 'Bookings will appear here once guests start making reservations.'
                : 'No bookings match your current filters.'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reference</TableHead>
                    <TableHead>Guest</TableHead>
                    <TableHead>Property / Room</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBookings.map((booking) => (
                    <TableRow key={booking.id} data-testid={`booking-row-${booking.booking_reference}`}>
                      <TableCell>
                        <span className="font-mono text-sm font-medium">
                          {booking.booking_reference}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{booking.guest_name}</p>
                          <p className="text-sm text-muted-foreground">{booking.guest_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{booking.property_name}</p>
                          <p className="text-sm text-muted-foreground">{booking.room_type_name}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{formatDateRange(booking.check_in, booking.check_out)}</p>
                          <p className="text-xs text-muted-foreground">{booking.nights} night(s)</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{formatCurrency(booking.total_amount, booking.currency || 'USD')}</span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={paymentStatusConfig[booking.payment_status]?.color || 'bg-gray-100'}
                        >
                          {paymentStatusConfig[booking.payment_status]?.label || booking.payment_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={statusConfig[booking.status]?.color || 'bg-gray-100'}
                        >
                          {statusConfig[booking.status]?.label || booking.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`booking-menu-${booking.booking_reference}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => viewDetails(booking)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setActionModal({ booking, tab: 'guest_info' })}>
                              <CalendarCheck className="h-4 w-4 mr-2" />
                              Manage Booking
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setActionModal({ booking, tab: 'history' })}>
                              <History className="h-4 w-4 mr-2" />
                              View History
                            </DropdownMenuItem>
                            {!booking.channex_synced && (
                              <DropdownMenuItem onClick={() => handleRetryChannex(booking.id)}>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Retry Channex Sync
                              </DropdownMenuItem>
                            )}
                            {booking.status !== 'completed' && booking.status !== 'cancelled' && (
                              <>
                                <DropdownMenuItem onClick={() => handleStatusUpdate(booking.id, 'completed')}>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Mark Completed
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleStatusUpdate(booking.id, 'cancelled')}
                                  className="text-destructive"
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Cancel Booking
                                </DropdownMenuItem>
                              </>
                            )}
                            {booking.status === 'confirmed' && (
                              <DropdownMenuItem onClick={() => handleStatusUpdate(booking.id, 'no_show')}>
                                <Clock className="h-4 w-4 mr-2" />
                                Mark No Show
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Booking details dialog */}
        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">Booking Details</DialogTitle>
            </DialogHeader>
            {selectedBooking && (
              <div className="space-y-6 mt-4">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-lg font-semibold">
                    {selectedBooking.booking_reference}
                  </span>
                  <Badge
                    variant="outline"
                    className={statusConfig[selectedBooking.status]?.color}
                  >
                    {statusConfig[selectedBooking.status]?.label}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Property</p>
                    <p className="font-medium">{selectedBooking.property_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Room Type</p>
                    <p className="font-medium">{selectedBooking.room_type_name}</p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm text-muted-foreground mb-3">Guest Information</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedBooking.guest_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedBooking.guest_email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedBooking.guest_phone}</span>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm text-muted-foreground mb-3">Stay Details</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Check-in</p>
                      <p className="font-medium">{formatDate(selectedBooking.check_in)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Check-out</p>
                      <p className="font-medium">{formatDate(selectedBooking.check_out)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Nights</p>
                      <p className="font-medium">{selectedBooking.nights}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Created</p>
                      <p className="font-medium">{formatDate(selectedBooking.created_at)}</p>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm text-muted-foreground mb-3">Payment</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-muted-foreground" />
                      <span className="capitalize">{selectedBooking.payment_method}</span>
                    </div>
                    <Badge
                      variant="outline"
                      className={paymentStatusConfig[selectedBooking.payment_status]?.color}
                    >
                      {paymentStatusConfig[selectedBooking.payment_status]?.label}
                    </Badge>
                  </div>
                  <div className="mt-4 p-4 bg-muted rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Total Amount</span>
                      <span className="text-xl font-semibold">
                        {formatCurrency(selectedBooking.total_amount, selectedBooking.currency || 'USD')}
                      </span>
                    </div>
                  </div>
                </div>

                {selectedBooking.notes && (
                  <div className="border-t pt-4">
                    <p className="text-sm text-muted-foreground mb-2">Notes</p>
                    <p className="text-sm">{selectedBooking.notes}</p>
                  </div>
                )}

                <div className="border-t pt-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Channex Sync:</span>
                    <Badge variant="outline" className={selectedBooking.channex_synced ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                      {selectedBooking.channex_synced ? 'Synced' : 'Pending'}
                    </Badge>
                  </div>
                  {!selectedBooking.channex_synced && (
                    <p className="text-xs text-muted-foreground">
                      Note: Ensure the Booking CRS App is installed in Channex dashboard for automatic sync.
                    </p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Unified Booking Action Modal */}
        {actionModal && (
          <BookingActionModal
            booking={actionModal.booking}
            initialTab={actionModal.tab}
            properties={properties}
            propertyTimezone={(() => { const p = properties.find(x => x.id === actionModal.booking.property_id); return p?.timezone; })()}
            onAmend={(id, data) => bookingsAPI.amend(id, data)}
            onCheckout={(id) => bookingsAPI.checkout(id)}
            onTransfer={(id, data) => bookingsAPI.transfer(id, data)}
            onClose={() => { setActionModal(null); fetchData(); }}
            onRefresh={fetchData}
          />
        )}
      </div>
    </AdminLayout>
  );
}
